import React, { useState } from 'react';
import { Controller, Control, FieldErrors, FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove } from 'react-hook-form';
import { TrashIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { RequiredIndicator } from '@/components/shared';
import { CollaboratorFormData, Child } from '@/features/Collaborators/Edit/Types';
import { sexOptions } from '@/data/commonDataOptions';

interface ChildrenFieldsetProps {
    context: CollaboratorFormContext;
    control: Control<CollaboratorFormData>;
    errors?: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
    layout?: 'full' | 'compact';
    fields: FieldArrayWithId<CollaboratorFormData, "children", "id">[];
    append: UseFieldArrayAppend<CollaboratorFormData, "children">;
    remove: UseFieldArrayRemove;
}

const ChildrenFieldset: React.FC<ChildrenFieldsetProps> = ({
    context,
    control,
    errors = {},
    isSubmitting = false,
    layout = 'full',
    fields,
    append,
    remove
}) => {
    const [newChild, setNewChild] = useState<Child>({ name: '', sex: 'Feminino', birthDate: '' });

    // Helper para verificar se campo deve aparecer usando helper global
    const getFieldError = (fieldName: string): string => {
        const fieldError = errors[fieldName as keyof FieldErrors<CollaboratorFormData>];
        if (fieldError && typeof fieldError === 'object' && 'message' in fieldError) {
            return String(fieldError.message || '');
        }
        return '';
    };

    // Handler para mudanças no novo filho
    const handleChildChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setNewChild((prev: Child) => ({ ...prev, [name]: value as any }));
    };

    // Handler para adicionar filho
    const handleAddChild = () => {
        if (!newChild.name || !newChild.birthDate) {
            return;
        }
        append(newChild);
        setNewChild({ name: '', sex: 'Feminino', birthDate: '' });
    };

    // Obter configuração contextual do layout
    const getLayoutByContext = (context: CollaboratorFormContext) => {
        const isCompact = context === 'PROFILE_UPDATE';
        return {
            fieldsetStyle: isCompact
                ? "border border-gray-200 p-3 rounded-md"
                : "border border-gray-200 p-4 rounded-md",
            legendStyle: isCompact
                ? "text-sm font-medium px-1 text-gray-600"
                : "text-sm font-medium px-1 text-gray-400"
        };
    };

    const layoutConfig = getLayoutByContext(context);

    // Verificar se o fieldset deve ser exibido usando schema unificado
    if (!collaboratorSchemaHelpers.shouldShowField('children', context)) {
        return null;
    }

    return (
        <fieldset className={layoutConfig.fieldsetStyle}>
            <legend className={layoutConfig.legendStyle}>
                Filhos
                {collaboratorSchemaHelpers.isFieldRequired('children', context) && <RequiredIndicator />}
            </legend>

            <div className="space-y-4">
                {/* Lista de Filhos Adicionados */}
                {fields.length > 0 && (
                    <div className="flow-root mt-4">
                        <ul className="divide-y divide-gray-200">
                            {fields.map((field, index) => (
                                <li key={field.id} className="py-2 flex items-center justify-between">
                                    <div className="text-sm">
                                        <Controller
                                            name={`children.${index}.name`}
                                            control={control}
                                            render={({ field: controllerField }) => (
                                                <span className="font-medium">{controllerField.value}</span>
                                            )}
                                        />
                                        {' - '}
                                        <Controller
                                            name={`children.${index}.sex`}
                                            control={control}
                                            render={({ field: controllerField }) => (
                                                <span className="text-gray-500">{controllerField.value}</span>
                                            )}
                                        />
                                        {' - '}
                                        <Controller
                                            name={`children.${index}.birthDate`}
                                            control={control}
                                            render={({ field: controllerField }) => (
                                                <span className="text-gray-500">{controllerField.value}</span>
                                            )}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="text-red-500 hover:text-red-700"
                                        disabled={isSubmitting}
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {fields.length === 0 && (
                    <p className="text-sm text-center py-2 text-gray-500">Nenhum filho adicionado.</p>
                )}

                {/* Formulário para Adicionar Filho */}
                <div className={`grid gap-4 items-end pt-2 border-t border-gray-200 mt-4 ${layout === 'compact' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-4'
                    }`}>
                    <div>
                        <label htmlFor="childName" className="block text-sm font-medium text-gray-700">
                            Nome do Filho(a)
                        </label>
                        <input
                            type="text"
                            name="name"
                            id="childName"
                            value={newChild.name}
                            onChange={handleChildChange}
                            className="input-primary w-full mt-1"
                            disabled={isSubmitting}
                        />
                    </div>

                    <div>
                        <label htmlFor="childSex" className="block text-sm font-medium text-gray-700">
                            Sexo
                        </label>
                        <select
                            name="sex"
                            id="childSex"
                            value={newChild.sex}
                            onChange={handleChildChange}
                            className="input-primary w-full mt-1"
                            disabled={isSubmitting}
                        >
                            {sexOptions.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label htmlFor="childBirthDate" className="block text-sm font-medium text-gray-700">
                            Data de Nascimento
                        </label>
                        <input
                            type="date"
                            name="birthDate"
                            id="childBirthDate"
                            value={newChild.birthDate}
                            onChange={handleChildChange}
                            className="input-primary w-full mt-1"
                            disabled={isSubmitting}
                        />
                    </div>

                    <div>
                        <button
                            type="button"
                            onClick={handleAddChild}
                            className="flex items-center gap-2 px-4 py-2 text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50 transition-colors w-full justify-center"
                            disabled={isSubmitting || !newChild.name || !newChild.birthDate}
                        >
                            <PlusCircleIcon className="h-5 w-5" />
                            Adicionar Filho
                        </button>
                    </div>
                </div>

                {/* Mostrar erros de validação */}
                {getFieldError('children') && (
                    <p className="mt-2 text-sm text-red-600">{getFieldError('children')}</p>
                )}
            </div>
        </fieldset>
    );
};

export default ChildrenFieldset; 